import { SagaIterator } from 'redux-saga';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import { Messages } from '../../../codegen/Localize';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { IScheduleInfo } from '../../../schedule/types/IScheduleInfo';
import { Set } from '../../../ts/models/Set';
import { UpdateScheduleRequest } from '../../../backgroundJob/types/UpdateScheduleRequest';
import { IResult } from '../../../httpClient/serverApi/types/IResponse';
import { IScheduleDetails, ScheduleAction } from '../../../schedule/types/ISchedule';
import { WebHttpClientWithErrorToast, WebHttpClientWithErrorToastInstance } from '../../../httpClient/WebHttpClientWithErrorToast';
import { RunSchedulesActionService } from '../../../schedule/actions/RunSchedulesAction';
import { RunServerSchedulesActionService } from '../../../schedule/actions/RunServerSchedulesAction';
import { SetScheduleStatusActionService } from '~src/schedule/actions/SetScheduleStatusAction';
import { ScheduleDisable, ScheduleEdit, ScheduleEnable, ScheduleRunNow } from '../../actions/AppActionTypes';
import { requestFailed } from '../../actions/RequestFailedAction';
import { requestSpaceContents } from '../../actions/spaces/RequestSpaceContentsActions';
import { ScheduleMenuAction } from '../../../schedule/actions/ScheduleMenuActions';
import { AdminSelfServiceScheduleDialog, AdminSelfServiceScheduleDialogState } from '../../content/AdminSelfServiceScheduleDialog';
import { NavLocationSelectors } from '../../selectors/NavigationStateSelectors';
import { ServiceLocator } from '../../ServiceLocator';
import { ScheduleCreateOrEditActionBody, ScheduleCreateOrEditActionBodyProps, StateForScheduleCreateOrEditActionBody } from '../../../schedule/components/ScheduleCreateEditActionBody';
import { openReactActionDialog } from '../../../commonInfra/dialogs/ReactActionDialog';
import { SelfServiceSchedulePaneType } from '@tableau/self-service-schedules/lib/src/components/SelfServiceSchedulePane';
import { TabDialogWidth } from '../../../commonInfra/dialogs/style/TabDialogWidth';
import { CreateOrUpdateScheduleActionService } from '../../../ts/services/CreateOrUpdateScheduleAction';
import { toNativePromise } from '~src/commonInfra/utils/QUtils';

export function runServerSchedules(schedules: IScheduleInfo[]) {
  return RunServerSchedulesActionService.execute(schedules);
}

export function runSiteSchedules(schedules: IScheduleInfo[]) {
  return RunSchedulesActionService.execute(schedules);
}

export function* handleRunNow(action: ScheduleMenuAction): SagaIterator {
  try {
    const inServerArea = yield select(NavLocationSelectors.inServerArea);
    if (inServerArea) {
      yield call(runServerSchedules, action.items);
    } else {
      yield call(runSiteSchedules, action.items);
    }

    yield put(requestSpaceContents());
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function enable(schedules: IScheduleInfo[]) {
  const context = ServiceLocator.getAngularActionContext();
  return SetScheduleStatusActionService.confirmEnableSchedules(Set.fromIdItemArray(schedules), context);
}

export function* handleEnable(action: ScheduleMenuAction): SagaIterator {
  try {
    yield call(enable, action.items);

    yield put(requestSpaceContents());
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function disable(schedules: IScheduleInfo[]) {
  const context = ServiceLocator.getAngularActionContext();
  return SetScheduleStatusActionService.confirmDisableSchedules(Set.fromIdItemArray(schedules), context);
}

export function* handleDisable(action: ScheduleMenuAction): SagaIterator {
  try {
    yield call(disable, action.items);

    yield put(requestSpaceContents());
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function editSelfServiceSchedule(schedule: IScheduleInfo): Promise<IResult> {
  const recurrence = IScheduleInfo.toSSSRecurrence(schedule);
  const initialDialogState: StateForScheduleCreateOrEditActionBody = {
    id: schedule.id,
    name: schedule.name,
    scheduledAction: schedule.scheduledAction as ScheduleAction,
    priority: schedule.priority,
    parallel: schedule.parallel,
    recurrence: recurrence,
    enabledForLinkedTasks: schedule.enabledForLinkedTasks
  };
  const initialBodyProps = {
    dialogMode: SelfServiceSchedulePaneType.AdminSchedule,
    isNew: false,
  };
  const action = (props: ScheduleCreateOrEditActionBodyProps, state: StateForScheduleCreateOrEditActionBody) => {
    const request = new UpdateScheduleRequest({
      name: state.name,
      scheduleType: state.recurrence.recurrenceType,
      scheduleDetails: state.recurrence.recurrenceDetails,
      priority: state.priority,
      parallel: state.parallel,
      id: state.id,
      enabledForLinkedTasks: state.enabledForLinkedTasks
    });
    return WebHttpClientWithErrorToastInstance.sendRequest(request).then(result => {
      CreateOrUpdateScheduleActionService.notifyResult(result, state.name, state.name);
      return result;
    });
  };
  const modalInstance = openReactActionDialog({
    title: Messages.adminSelfServiceSchedulesDialogEditTitle(),
    bodyComponent: ScheduleCreateOrEditActionBody,
    initialBodyProps: initialBodyProps,
    initialDialogState: initialDialogState,
    actionName: Messages.adminSelfServiceSchedulesDialogConfirmUpdate(),
    action: action,
    actionDisabled: (props, state) => state.name.length === 0,
    dismissDialogOnActionFailed: true,
    dialogWidth: TabDialogWidth.width500px,
  });
  return toNativePromise(modalInstance.result);
}

export function edit(schedules: IScheduleInfo[]) {
  const schedule = schedules[0];
  if (FeatureFlags.adminSelfServiceSchedulesEnabled) {
    return editSelfServiceSchedule(schedule);
  } else {
    return CreateOrUpdateScheduleActionService.editSchedule(schedule);
  }
}

export function* handleEdit(action: ScheduleMenuAction): SagaIterator {
  try {
    if (action.items.length > 0) {
      yield call(edit, action.items);
    }

    yield put(requestSpaceContents());
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* rootScheduleMenuActionSaga(): SagaIterator {
  yield takeEvery(ScheduleRunNow, handleRunNow);
  yield takeEvery(ScheduleEnable, handleEnable);
  yield takeEvery(ScheduleDisable, handleDisable);
  yield takeEvery(ScheduleEdit, handleEdit);
}
